import { ComboboxField } from "@repo/ui/combobox/comboboxField";
import { useMemo } from "react";
import type { Key } from "react-aria-components";

interface BaseTimezoneSelectProps {
  label?: string;
  isDisabled?: boolean;
  placeholder?: string;
}

type TimezoneSelectProps =
  | (BaseTimezoneSelectProps & {
      value: string;
      onChange: (value: string) => void;
      defaultOptionLabel?: undefined;
    })
  | (BaseTimezoneSelectProps & {
      value: string | null;
      onChange: (value: string | null) => void;
      defaultOptionLabel: string;
    });

export interface TimezoneOption {
  id: string;
  name: string;
}

const DEFAULT_TIMEZONE_OPTION_ID = "__default_timezone__";

export function getTimezoneOptions(): TimezoneOption[] {
  const timezones = Intl.supportedValuesOf("timeZone");

  return timezones
    .map((timezone) => {
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        timeZoneName: "longOffset",
      });

      const parts = formatter.formatToParts();
      const offsetPart =
        parts.find((part) => part.type === "timeZoneName")?.value || "";

      const formattedName = timezone
        .split("/")
        .map((part) => part.replace(/_/g, " "))
        .join(" / ");

      return {
        id: timezone,
        name: `${formattedName} ${offsetPart}`,
      };
    })
    .sort((a, b) => {
      const offsetA = a.name.match(/GMT([+-]\d{2}:\d{2})/)?.[1] || "";
      const offsetB = b.name.match(/GMT([+-]\d{2}:\d{2})/)?.[1] || "";
      if (offsetA !== offsetB) {
        return offsetA.localeCompare(offsetB);
      }
      return a.name.localeCompare(b.name);
    });
}

export const TimezoneSelect = (props: TimezoneSelectProps) => {
  const {
    value,
    label,
    isDisabled = false,
    placeholder = "Select timezone",
    defaultOptionLabel,
  } = props;

  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);
  const options = useMemo(
    () =>
      defaultOptionLabel
        ? [
            {
              id: DEFAULT_TIMEZONE_OPTION_ID,
              name: defaultOptionLabel,
            },
            ...timezoneOptions,
          ]
        : timezoneOptions,
    [defaultOptionLabel, timezoneOptions],
  );

  const handleChange = (key: Key | null) => {
    // Clearing the text reports no selection. Keep the current timezone rather
    // than persisting "none": the input reverts to it when focus leaves.
    if (key === null) {
      return;
    }

    const nextValue = key === DEFAULT_TIMEZONE_OPTION_ID ? null : String(key);

    if (defaultOptionLabel !== undefined) {
      props.onChange(nextValue);
    } else if (nextValue !== null) {
      props.onChange(nextValue);
    }
  };

  return (
    <div className="w-[300px]">
      <ComboboxField<TimezoneOption>
        label={label}
        aria-label={label ? undefined : "Timezone"}
        items={options}
        getLabel={(option) => option.name}
        value={value ?? DEFAULT_TIMEZONE_OPTION_ID}
        onChange={handleChange}
        placeholder={placeholder}
        isDisabled={isDisabled}
      />
    </div>
  );
};
